import React, {useState} from 'react';
import {OperatorAudienceSettings, OperatorAudienceSettingsData} from "types/Operator";
import {Form, Formik, FormikHelpers} from "formik";
import FormControls from "components/FormControls";
import {useMutation, useQuery} from "react-query";
import ngage from "../../../services/ngage";
import {MultiSelectField} from "../../../components/Form/Form";
import {toTitleCase} from "../../../services/string";
import {AttributeData, AttributeType} from "../../../types/Attribute";
import IconName from "../../../types/IconName";
import Button from "../../../components/Button";
import {PlusSmIcon} from "@heroicons/react/solid";
import ShortcutModal from "../../../components/Shortcut/ShortcutModal";
import AttributeForm from "../../Attribute/Form";
import {invalidate} from "../../../services/query-client";
import {ToggleSwitch} from "../../../components/Form/Form/Control";

// Types ----

type AudienceFormProps = {
  settings?: OperatorAudienceSettings | null
  onSubmit: (values: OperatorAudienceSettingsData, helpers: FormikHelpers<OperatorAudienceSettingsData>) => any
}

// Helpers ----

const attributeTypeIcons: Record<AttributeType, IconName> = {
  [AttributeType.BOOLEAN]: IconName.CHECK_CIRCLE,
  [AttributeType.DATE]: IconName.CALENDAR,
  [AttributeType.SELECT]: IconName.SELECTOR,
  [AttributeType.STRING]: IconName.PENCIL,
}

// Component ----

function AudienceForm({onSubmit, settings}: AudienceFormProps) {
  const [addingAttribute, setAddingAttribute] = useState<boolean>(false);

  const {data: attributes, isLoading: isLoadingAttributes} = useQuery(
    ['attributes', 'all'],
    () => ngage.attributes.filtered({sort: 'name'})
  );

  const {mutateAsync: storeAttribute} = useMutation(
    (data: AttributeData) => ngage.attributes.store(data)
  );

  async function addAttribute(values: AttributeData) {
    await storeAttribute(values)
    await invalidate('attributes')
    setAddingAttribute(false)
  }

  const attributeOptions = attributes?.data.map(attribute => ({
    type: 'attribute',
    id: attribute.id,
    name: attribute.name,
    description: toTitleCase(attribute.type),
    icon: attributeTypeIcons[attribute.type]
  })) ?? [];

  const initialValues: OperatorAudienceSettingsData = {
    attribute_ids: settings?.attribute_ids || [],
    guest_users_enabled: settings?.guest_users_enabled || false,
  }

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      {formik => (
        <section aria-labelledby="audience-app-heading">
          <Form onKeyDown={event => {
            if (event.ctrlKey && event.key === 'Enter') formik.handleSubmit()
          }}>
            <div className="shadow sm:rounded-md">
              <div className="bg-white sm:rounded-t-md py-6 px-4 sm:p-6">
                <div>
                  <h2 id="audience-app-heading" className="text-lg leading-6 font-medium text-gray-900">
                    Audience
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Configure your audience data
                  </p>
                </div>

                <div className="mt-6 grid grid-cols-6 gap-6">
                  {/* Extra Attributes */}
                  <div className="col-span-6">
                    <fieldset className="space-y-4">
                      <div>
                        <legend className="text-base font-medium text-gray-900">Extra Attributes</legend>
                        <p className="text-sm text-gray-500">
                          Add and select additional attributes to record additional data for your audience members
                        </p>
                      </div>

                      <div className="space-y-4 md:space-y-0">
                        <MultiSelectField
                          name="attribute_ids"
                          label="Enabled Attributes"
                          hint="optional"
                          help="You can select up to 3 attributes to enable at once"
                          options={attributeOptions}
                          isLoading={isLoadingAttributes}
                          max={3}
                        />
                      </div>

                      <div>
                        <Button
                          color={Button.colors.WHITE}
                          size={Button.sizes.X_SMALL}
                          icon={PlusSmIcon}
                          onClick={() => setAddingAttribute(true)}
                        >
                          Add Attribute
                        </Button>
                      </div>

                    </fieldset>
                  </div>

                  {/* Guests */}
                  <div className="col-span-6">
                    <fieldset className="space-y-4">
                      <div>
                        <legend className="text-base font-medium text-gray-900">Guests</legend>
                      </div>

                      {/* Enable guest users */}
                      <div className="col-span-6 md:col-span-6">
                        <ToggleSwitch
                          label="Guest users"
                          description="When enabled, guests can use the app without signing up"
                          checked={formik.values.guest_users_enabled}
                          onChange={enabled => formik.setFieldValue('guest_users_enabled', enabled)}
                          multiline
                        />
                      </div>
                    </fieldset>
                  </div>
                </div>
              </div>

              <FormControls formik={formik} compact withoutCancel/>
            </div>

            <ShortcutModal show={addingAttribute} close={setAddingAttribute}>
              <AttributeForm onCancel={() => setAddingAttribute(false)} onSubmit={addAttribute}/>
            </ShortcutModal>
          </Form>
        </section>
      )}
    </Formik>
  );
}

// Export ----

export default AudienceForm;